import { createContext, useEffect, useState, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import { useLocation } from "react-router-dom";
import api from "../utils/api";

const Context = createContext();

function UserProvider({ children }) {
    const { authenticated, loading, login, logout, loggedInUser } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [activeItem, setActiveItem] = useState('dashboard');
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);
    const [permissions, setPermissions] = useState(null);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [foundGroups, setFoundGroups] = useState({});
    const [selectedType, setSelectedType] = useState('Expedição');

    const [router] = useState({
        dashboard: {
            path: '/',
            resource: 'dashBoard',
            label: 'Dashboard'
        },
        load: {
            path: '/load',
            resource: 'upload',
            label: 'Carregar Dados'
        },
        analytics: {
            path: '/analytics',
            resource: 'analytics',
            label: 'Análise'
        },
        export: {
            path: '/export',
            resource: 'export',
            label: 'Exportar'
        },
        ticketUpdates: {
            path: '/ticketUpdates',
            resource: 'ticketUpdates',
            label: 'Atualizações de Tickets'
        },
        notifications: {
            path: '/notifications',
            resource: 'notifications',
            label: 'Notificações'
        },
        profile: {
            path: '/profile',
            resource: null, // Sempre acessível
            label: 'Perfil'
        },
        settings: {
            path: '/settings',
            resource: 'settings',
            label: 'Configurações'
        },
        logout: {
            path: '/login',
            resource: null,
            label: 'Sair'
        }
    });

    // ========================================
    // CARREGAR PERMISSÕES DO BACKEND
    // ========================================
    const loadPermissions = useCallback(async () => {
        if (!loggedInUser) {
            setPermissionsLoading(false);
            return;
        }

        try {
            setPermissionsLoading(true);
            const response = await api.get('/permissions-check/my-permissions');

            if (response.data.success) {
                setPermissions(response.data.data);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar permissões:', error);
        } finally {
            setPermissionsLoading(false);
        }
    }, [loggedInUser]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    // ========================================
    // VERIFICAÇÃO DE PERMISSÃO
    // ========================================
    const hasPermission = useCallback((resource) => {
        if (!permissions || !permissions.resources) return false;

        // Se vier como Map
        if (permissions.resources instanceof Map) {
            return permissions.resources.get(resource) === true;
        }

        // Se vier como objeto normal
        return permissions.resources[resource] === true;
    }, [permissions]);


    const hasRoutePermission = useCallback((routeKey) => {
        const route = router[routeKey];
        if (!route || !route.resource) return true;
        return hasPermission(route.resource);
    }, [router, hasPermission]);

    // ========================================
    // NOTIFICAÇÕES
    // ========================================
    const refreshUnreadCount = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            if (response.data.success) {
                setUnreadCount(response.data.unreadCount);
            }
        } catch (error) {
            console.error('❌ Erro ao atualizar notificações:', error);
        }
    }, []);

    useEffect(() => {
        if (loggedInUser) {
            refreshUnreadCount();
            const interval = setInterval(refreshUnreadCount, 30000);
            return () => clearInterval(interval);
        }
    }, [loggedInUser, refreshUnreadCount]);

    // ========================================
    // ACTIVE ITEM
    // ========================================
    useEffect(() => {
        let path = location.pathname;
        path = path.replace(/^\/+/, "");
        if (path === "") path = "dashboard";
        setActiveItem(path);
    }, [location.pathname]);

    const [stats, setStats] = useState({
        totalScanned: 0,
        found: 0,
        notFound: 0,
        totalNC: 0,
        uniqueCount: 0
    });



    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/stats');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao buscar estatísticas:', error);
        }
    }, []); // sem deps pois só usa setStats (estável)

    return (
        <Context.Provider value={{
            authenticated,
            loading,
            login,
            logout,
            isOpen,
            setIsOpen,
            activeItem,
            setActiveItem,
            loggedInUser,
            refreshUnreadCount,
            unreadCount,
            router,
            permissions,
            permissionsLoading,
            hasPermission,
            hasRoutePermission,
            loadPermissions,
            selectedType,
            setSelectedType,
            history,
            setHistory,
            foundGroups,
            setFoundGroups,
            stats,
            setStats,
            fetchStats
        }}>
            {children}
        </Context.Provider>
    );
}

export { Context, UserProvider };